/* eslint-disable no-else-return */
type ValueGetter<T = any> = (item: T) => string | number | Date | null;
type SortingOrder = 'ascending' | 'descending';

interface Item<T = any> {
  [key: string]: T;
}

// eslint-disable-next-line
/**
 * Generic Sortby Method. Also puts null values to bottom of array
 * @param {T[]} array - The array to sort.
 * @param {ValueGetter<T>} key - Value getter func.
 * @param {SortingOrder} [order=ascending] - SortingOrder = 'ascending'
 * @param {boolean} [nullLast=true] - "Null values should come first or last"
 * @returns An array of sorted items.
 *
 * @example
 * ```ts
 *    sortBy(
 *         products,
 *         item => item.product_symbol.toLowerCase(),
 *         "descending"
 *     )
 * ```
 */
const sortBy = <T extends Item>(
  array: T[],
  key: ValueGetter<T>,
  order: SortingOrder = 'ascending',
  nullLast: boolean = true
) => {
  const sorter = (a: T, b: T) => {
    const valueA = key(a);
    const valueB = key(b);
    if (valueA === valueB) {
      return 0;
    } else if (valueA === null) {
      return nullLast ? 1 : -1;
    } else if (valueB === null) {
      return nullLast ? -1 : 1;
    } else if (order === 'ascending') {
      return valueA < valueB ? -1 : 1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  };
  return [...array].sort(sorter);
};

export { sortBy };
export type { ValueGetter, SortingOrder, Item };
